import Backbone from 'backbone'
import template from './button-widget.hbs'
import BusyButton from 'scoreboard/components/busy-button'
import _ from 'lodash'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import Tooltip from 'scoreboard/components/tooltip'
import EvaluateStatementsActor from 'scoreboard/forms/actions/evaluate-statements-actor'
import { recordInsertionNotAllowedMessage } from 'scoreboard/forms/actions/save-record-actor'
import { isSaveGuaranteed } from 'scoreboard/forms/model/form'
import FormActionType from 'scoreboard/forms/model/form-action-type'
import {getDialogDatasetOrDataset} from '../edit/pages-edit'
const { SAVE_RECORD } = FormActionType

const ButtonWidget = Backbone.View.extend({

	tagName: 'li',

	className: 'button-widget',

	/**
	 * A button widget in the forms section
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model
	 *
	 * @param {FormWidget} options.widget the data for the widget
	 *
	 * @param {FormView} options.formView the FormView this form is
	 * participating in
	 *
	 * @param {FormDirector} options.formDirector
	 */
	initialize: function(options = {}) {
		this.options = options
		this.dataset = getDialogDatasetOrDataset(this.model)
		this.formView = options.formView
	},

	events: {
		'click .actual-button': 'onClickActualButton'
	},

	render: function() {
		this.$el.empty().append(template(this.options.widget))
		let actualButton = this.$('.actual-button')

		let appearance = this.options.widget?.appearance
		if (typeof appearance === 'undefined' || appearance === 'primary') {
			actualButton
				.addClass('button-primary')
				.removeClass('button-default')
		} else {
			actualButton.addClass('button-secondary')
		}

		let isDisabled = this.options.widget?.state === 'disabled' ||
			(isSaveGuaranteed(this.options.widget.statements) &&
				!this.dataset.isModifyRecords())
		actualButton
			.toggleClass('is-disabled', isDisabled)
			.prop('disabled', isDisabled)
		this.handleSaveRecordAction(actualButton)
		this.busyButton = new BusyButton({ el: actualButton })
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return this
	},

	isSaveAllowed: function() {
		let newRecordsAllowed = this.dataset.get('newAllowed')
		let modifyingRecord = !_.isEmpty(_.invoke(this.options.formDirector,
			'getRecord'))
		return newRecordsAllowed || modifyingRecord
	},

	handleSaveRecordAction: function(actualButton) {
		if (this.isSaveRecordAction()) {
			actualButton.attr('type', 'submit')
		}
	},

	isSaveRecordAction: function(options={}) {
		return this.hasAction(SAVE_RECORD, options)
	},

	/**
	 * Returns whether this widget has an action of the specified type
	 * associated with it
	 */
	hasAction: function(type, options={}) {
		const actions = options.actions ?? this.options.widget.actions
		return _.some(actions, action => action.type === type)
	},

	onClickActualButton: async function() {
		const actor = new EvaluateStatementsActor({
			formDirector: this.options.formDirector,
			model: this.model,
			formView: this.formView,
			widget: this.options.widget
		})
		let statementGroups =
			this.options.widget?.statements ?? this.model?.statements
		this.currentActor = actor

		// #62630 loop checks statements in isolation, check all actions
		// here instead of inside the loop to handle go-to and save actions
		const { hasGoToPage, hasSaveRecord } =
			this.options.formDirector.hasActions(await actor.act())

		// #59051
		// Similar to #56953, execute each statement group on a button
		// in isolation so that the next group has the context of the previous.
		let i = 0
		do {
			const statements = statementGroups[i]
			const actions = await actor.act(
				_.size(statementGroups) == 0 ? statementGroups : [statements]
			)

			// 53226 avoid race conditions
			if (this.currentActor !== actor) return

			const actualButton = this.$('.actual-button')
			const modifyNotAllowed = !this.dataset.isModifyRecords() &&
				!hasGoToPage
			const saveNotAllowed = hasSaveRecord
				&& !this.isSaveAllowed()
			if (saveNotAllowed) {
				this.showDisabledTooltip(actualButton)

				// if the save is not allowed, it is because the dataset
				// doesn't allow it. That cannot change while we are still
				// on this page. The button will also result in the same
				// action until we edit it. So, we can disable the button
				// and add the tooltip permanently to avoid evaluating the
				// statements every time the button is clicked
				actualButton.on('click', () =>
					this.showDisabledTooltip(actualButton))
			}

			if (modifyNotAllowed || saveNotAllowed) {
				actualButton
					.addClass('is-disabled')
					.attr('type', 'button')
				return
			}
			// #62630 await to avoid race conditions
			await this.options.formDirector.direct(actions,
				{ busyButton: this.busyButton, hasGoToPage, hasSaveRecord })
			i++
		} while (i < statementGroups.length)
	},

	showDisabledTooltip: function(button) {
		this.disabledTooltip?.remove()

		this.disabledTooltip = new Tooltip({
			closeOnChangedFocus: true,
			attachTo: button,
			placement: 'right',
			arrow: 'center',
			content: recordInsertionNotAllowedMessage(this.dataset.get('label'),
				this.dataset.get('id'))
		}).render()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.busyButton, this.covertButtonTooltips, this.disabledTooltip], 'remove')
	}

})

export default ButtonWidget
ButtonWidget.__filename__ = 'forms/widgets/button-widget.js'
